import _ from "lodash";
import React, { useState, useEffect, memo } from "react";
import classNames from "classnames";
import { IconButton } from "@toddle-edu/ds-web";
import {
  DeleteOutlined,
  BentArrowBottomLeftOutlined,
  BulletDotFilled,
} from "@toddle-edu/ds-icons";
import classes from "./ListItem.scss";
import { colors } from "@toddle-edu/ds-theme";
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";
import { generateRandomId } from "Utils";
import { useI18n } from "Hooks";

const styles = {
  contentEditable: {
    padding: "0px",
    fontSize: "1.6rem",
    fontWeight: 500,
    lineHeight: "2rem",
    color: colors.textDefault,
    minHeight: "auto",
    whiteSpace: "pre-wrap",
  },
};

const ListItem = memo(props => {
  const {
    item,
    value: itemList,
    index,
    onChange,
    mode = "edit",
    contentEditableRefs,
    customContentEditableStyle = {},
    minItemListLength = 1,
    placeholder,
    isWorkbookMode = false,
    showOrderedList = false,
  } = props;

  const [currentActiveItem, setCurrentActiveItem] = useState("");
  const { t } = useI18n(["common"]);

  useEffect(() => {
    if (currentActiveItem) {
      contentEditableRefs?.current?.[currentActiveItem]?.focus();
    }
  }, [currentActiveItem]);

  const shouldRestrictAddButton =
    _.size(itemList) > 0 && _.last(itemList)?.value === "";

  const isItemActive = currentActiveItem === item.id;

  const onTextChange = value => {
    const filteredValue = _.replace(value, /&nbsp;/g, " ");
    const updatedItemValue = { ...item, value: filteredValue };
    const updatedItemList = _.map(itemList, listItem => {
      return item.id === listItem.id ? updatedItemValue : listItem;
    });
    onChange(updatedItemList);
  };

  const onAddItem = params => {
    const { insertAt } = params || {};

    const newItem = {
      id: generateRandomId(),
      value: "",
      isSelected: true,
    };

    if (!_.isNil(insertAt)) {
      const updatedItemList = [
        ...itemList.slice(0, insertAt),
        newItem,
        ...itemList.slice(insertAt),
      ];

      onChange(updatedItemList);
    } else {
      const updatedItemList = [...itemList, newItem];
      onChange(updatedItemList);
    }

    setCurrentActiveItem(newItem.id);
  };

  const onKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      const currentActiveItemIndex = _.findIndex(
        itemList,
        item => item.id === currentActiveItem
      );

      if (currentActiveItemIndex === _.size(itemList) - 1) {
        onAddItem();
      } else {
        const nextItem = itemList?.[currentActiveItemIndex + 1];
        if (nextItem?.id) {
          setCurrentActiveItem(nextItem.id);
        }
      }
    }
  };

  const onDeleteItem = () => {
    // delete only if there is more than one item in the list
    if (itemList.length === 1) {
      return;
    }

    const updatedItemList = _.filter(
      itemList,
      listItem => listItem.id !== item.id
    );

    onChange(updatedItemList);
  };

  const separatorClass = classNames({
    [classes.separator]: mode === "edit",
    [classes.separatorActive]: isItemActive && mode === "edit",
    [classes.viewModeSeparator]: mode === "view",
  });

  const listItemContainerClass = classNames({
    [classes.listItemContainer]: true,
    [classes.listItemContainerWorkbook]: isWorkbookMode,
    [classes.listItemContainerWithoutPadding]:
      showOrderedList && mode === "view",
  });

  const bulletDotClass = classNames({
    [classes.bulletDot]: true,
    [classes.bulletDotWorkbook]: isWorkbookMode,
  });

  const contentEditableStyles = {
    ...styles.contentEditable,
    ...customContentEditableStyle,
  };

  return (
    <div className={classes.listItemWrapper}>
      <div className={listItemContainerClass}>
        <div className={classes.listItemLeftContainer}>
          {showOrderedList && mode === "view" ? (
            <div style={contentEditableStyles}>{`${index + 1}.`}</div>
          ) : (
            <BulletDotFilled
              variant="subtle"
              size="xx-small"
              className={bulletDotClass}
            />
          )}
          <div className={classes.contentEditable}>
            <UIBaseContentEditable
              value={item.value}
              placeholder={
                _.isFunction(placeholder)
                  ? placeholder?.(index)
                  : t("common:typeHere")
              }
              updateInputField={onTextChange}
              onBlurInputField={() => setCurrentActiveItem(null)}
              onFocusInputField={() => setCurrentActiveItem(item.id)}
              updateInputRef={ref => {
                if (ref) {
                  contentEditableRefs.current[item.id] = ref;
                }
              }}
              onKeyDown={onKeyDown}
              style={contentEditableStyles}
              disabled={mode === "view"}
            />
          </div>
        </div>

        {mode === "edit" ? (
          <div className={classes.listItemRightContainer}>
            <IconButton
              dsVersion="2.0"
              icon={<BentArrowBottomLeftOutlined variant="subtle" />}
              onClick={() => onAddItem({ insertAt: index + 1 })}
              variant="neutral"
              type="plain"
              size="small"
              isCompact={true}
              disabled={
                shouldRestrictAddButton && index === _.size(itemList) - 1
              }
            />
            <IconButton
              dsVersion="2.0"
              icon={<DeleteOutlined variant="subtle" />}
              onClick={onDeleteItem}
              variant="neutral"
              type="plain"
              size="small"
              isCompact={true}
              disabled={_.size(itemList) === minItemListLength}
            />
          </div>
        ) : null}
      </div>

      <div className={separatorClass} />
    </div>
  );
});

ListItem.displayName = "ListItem";

export default ListItem;
